import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useMetadataStore } from '@/entities/metadata/model/metadataStore';
import type { ObjectBundle } from '@/entities/metadata/api/useScreenBundle';
import { queryKeys } from '@/shared/api/queryKeys';
import { useOpenScreen } from './useOpenScreen';
import { useTabsStore } from './tabsStore';
import { objectTabSpec, type ObjectTabFilter } from './lib/objectTab';
import type { UserScreenNode } from '@/shared/types/system';

/** Menü ağacında verilen nesneyi gösteren ilk ekranı bulur. */
function findByObject(nodes: UserScreenNode[], objectId: string): UserScreenNode | null {
  for (const n of nodes) {
    if ((n.objectId ?? '').toLowerCase() === objectId.toLowerCase()) return n;
    const hit = findByObject(n.children, objectId);
    if (hit) return hit;
  }
  return null;
}

/**
 * Bir NESNEYİ uygulamanın kendi sekmesinde açar.
 *
 * `open`: menüdeki ekranı açar/odaklar; ekranı yoksa `false` döner ve çağıran karar verir
 * (ör. AI sohbeti bağlantıyı hiç göstermez — "açar ama boş ekran gelir" olmasın).
 *
 * `openFiltered`: her zaman açar. Ekranı varsa o ekran yeni sekmede (#445), yoksa nesne
 * ObjectId ile kendi sekmesinde (#533) — SP formu ve detay sekmeleri de nesneyi ekransız
 * açıyor, yetki nesne üzerinden; her sonuç nesnesi için menüye yapay ekran gerekmesin.
 */
export function useOpenObjectScreen() {
  const menuTree = useMetadataStore((s) => s.menuTree);
  const openScreen = useOpenScreen();
  const openCustom = useTabsStore((s) => s.openCustom);
  const qc = useQueryClient();
  const { t } = useTranslation();

  const screenFor = useCallback(
    (objectId: string | null | undefined) => (objectId ? findByObject(menuTree, objectId) : null),
    [menuTree],
  );

  const open = useCallback(
    (objectId: string | null | undefined) => {
      const node = screenFor(objectId);
      if (node) openScreen(node);
      return !!node;
    },
    [screenFor, openScreen],
  );

  /**
   * Nesneyi her zaman YENİ bir sekmede, verilen filtreyle açar (#445, #533).
   *
   * Popup'ın "yeni sekmede aç" düğmesi için: hedef ekran zaten açıksa (çoğu zaman işlemi
   * çalıştıran ekranın kendisi) `open` yalnızca o sekmeyi odaklıyordu — kullanıcı için
   * "hiçbir şey olmadı". Filtre de o sekmeye taşınamazdı; ayrı sekme ikisini de çözer.
   */
  const openFiltered = useCallback(
    (
      objectId: string | null | undefined,
      filter: ObjectTabFilter,
      opts?: {
        withDetails?: boolean;
        activate?: boolean;
        /** Ekransız nesne sekmesinin adı (popup başlığı); yoksa nesne adı / genel ad. */
        title?: string | null;
        /** SP @MaxRecords: ekransız sekmede TOP N korunur. */
        maxRecords?: number | null;
      },
    ) => {
      if (!objectId) return false;
      const found = screenFor(objectId);
      if (!found) {
        // Ekran kaydı yok → nesne sekmesi (#533). Ad: popup başlığı, yoksa önbellekteki
        // nesne paketinin takma adı (popup onu zaten indirmişti), o da yoksa genel ad.
        const cached = qc.getQueryData<ObjectBundle>(queryKeys.meta.objectBundle(objectId));
        const caption =
          opts?.title?.trim() ||
          cached?.object?.objectAlias ||
          cached?.object?.caption ||
          t('actions.result.tabTitle');
        openCustom(objectTabSpec(objectId, caption, filter, { maxRecords: opts?.maxRecords }), {
          activate: opts?.activate,
        });
        return true;
      }
      /*
       * Nesnenin detay sekmeleri varsa sekme master-detay açılır (#445): menüdeki ekran tek
       * grid tipindeyse popup detaylı görünürken sekmede yalnız master geliyordu.
       * MasterDetailScreen detay yoksa zaten tek grid'e düşüyor.
       */
      const node =
        opts?.withDetails && (found.screenTypeName ?? '').toLowerCase() === 'master'
          ? { ...found, screenTypeName: 'MasterDetail' }
          : found;
      const params: Record<string, unknown> = {};
      if (filter.whereClause) params['whereClause'] = filter.whereClause;
      if (filter.hedefFilter) {
        params['hedefFilter'] = filter.hedefFilter;
        params['hedefFilterValue'] = filter.hedefFilterValue;
      }
      openScreen(node, params, { focusExisting: false, activate: opts?.activate });
      return true;
    },
    [screenFor, openScreen, openCustom, qc, t],
  );

  return { screenFor, open, openFiltered };
}
